// Originally a port of legacy
// frontend/src/pages/Category/PantryCategory/context.tsx. Subsequent updates:
//   - Source the active store via `usePantryStore()` (provider injected by
//     the consuming app) instead of `useAuth/useGetStore/useUser` Redux
//     hooks — pantry-features cannot import from the main app's ~/redux
//     tree.
//   - Consume the gateway-backed hooks from `../hooks/useCategory`.
//   - 2026-06-01: Mapping tab moved off the embedded
//     cova_category/greenline_category/pos_mapping arrays. `linkedPosCategories`
//     is now derived from the unified many-to-many
//     GET /pantry/stores/:storeId/category-mapping endpoint (via
//     `useCategoryMapping`), and `saveCategoryMappings` writes through the
//     matching PUT endpoint (via `useSaveCategoryMapping`) as a flat
//     posId → webIds[] dict. The Cova-specific
//     /cova/pre-onboarding/classifications/update endpoint and its
//     companyId/src/dest payload shape are no longer involved on the save
//     path. `useWebCategoriesWithPOS` and `usePOSClassifications` still
//     hydrate the Web tab and POS tab respectively.

import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { usePantryStore } from '../../../providers/PantryProvider'
import {
  useBulkDeleteCategories,
  useBulkUpdateVisibility,
  useCategoryMapping,
  useCreateCategory,
  useDeleteCategory,
  usePOSClassifications,
  useSaveCategoryMapping,
  useUpdateCategory,
  useWebCategoriesWithPOS,
} from '../hooks/useCategory'
import type {
  CategoryMappingDict,
  LinkedPosCategoriesMap,
  LinkedPosCategoryRef,
  LinkedWebCategoryRef,
  Mapping,
  POSCategory,
  PantryCategoryFormValues,
  PosMappingsByWebId,
  WebCategory,
} from '../types'
import { storeDomainFromUrl } from '../utils/storeDomain'
import { STORE_TYPES } from '../utils/storeTypes'

interface CategoryContextValue {
  webCategories: WebCategory[]
  posCategories: POSCategory[]
  posTotalProducts: number
  linkedPosCategories: LinkedPosCategoriesMap
  posMappingsByWebId: PosMappingsByWebId
  isLoadingWeb: boolean
  isLoadingPOS: boolean
  isCreatingCategory: boolean
  isUpdatingCategory: boolean
  isDeletingCategory: boolean
  isSavingMappings: boolean
  isBulkUpdatingVisibility: boolean
  isBulkDeleting: boolean
  isMutatingCategory: boolean
  setWebCategories: Dispatch<SetStateAction<WebCategory[]>>
  createCategory: (input: PantryCategoryFormValues) => Promise<unknown>
  updateCategory: (
    categoryId: string,
    input: PantryCategoryFormValues,
  ) => Promise<unknown>
  deleteCategory: (categoryId: string) => Promise<unknown>
  bulkUpdateVisibility: (ids: string[], visible: boolean) => Promise<unknown>
  bulkDeleteCategories: (ids: string[]) => Promise<unknown>
  saveCategoryMappings: (mappings: Mapping[]) => Promise<unknown>
  storeDomain: string
  storeType: string
}

const CategoryContext = createContext<CategoryContextValue>({
  webCategories: [],
  posCategories: [],
  posTotalProducts: 0,
  linkedPosCategories: {},
  posMappingsByWebId: {},
  isLoadingWeb: false,
  isLoadingPOS: false,
  isCreatingCategory: false,
  isUpdatingCategory: false,
  isDeletingCategory: false,
  isSavingMappings: false,
  isBulkUpdatingVisibility: false,
  isBulkDeleting: false,
  isMutatingCategory: false,
  setWebCategories: () => {},
  createCategory: async () => ({}),
  updateCategory: async () => ({}),
  deleteCategory: async () => ({}),
  bulkUpdateVisibility: async () => ({}),
  bulkDeleteCategories: async () => ({}),
  saveCategoryMappings: async () => ({}),
  storeDomain: '',
  storeType: '',
})

export const usePantryCategory = () => useContext(CategoryContext)

interface CategoryProviderProps {
  children: ReactNode
}

export const CategoryProvider = ({ children }: CategoryProviderProps) => {
  const pantryStore = usePantryStore()
  const storeId = pantryStore?.storeId ?? ''
  const orgId = pantryStore?.orgId ?? ''
  const storeUrl = pantryStore?.storeUrl ?? ''
  const storeType = pantryStore?.storeType ?? ''

  // Auth normalizes API `company_id` → `companyId` (see
  // frontend/src/modules/auth/utils/index.ts:67). Coerce to a finite number
  // because the Cova pre-onboarding endpoint expects a numeric company_id.
  const rawCompanyId = pantryStore?.companyId
  const companyIdForApi = useMemo(() => {
    if (rawCompanyId == null || rawCompanyId === '') return undefined
    const parsed = Number(rawCompanyId)
    return Number.isFinite(parsed) ? parsed : undefined
  }, [rawCompanyId])

  const { data: fetchedWebCategories = [], isLoading: isLoadingWeb } =
    useWebCategoriesWithPOS(storeId)

  // POS-tree read payload. Cova requires org + numeric company_id; Greenline
  // only needs storeId (the bs-store-id header carries the company scope).
  // The hook's `enabled` predicate enforces the per-POS-type contract — we
  // still build the payload so the storeType change is reflected in the
  // React Query cache key.
  const posClassificationsPayload = useMemo(() => {
    if (!storeId) return null
    const normalizedStoreType = (storeType || '').toLowerCase()
    if (normalizedStoreType === STORE_TYPES.GREENLINE) {
      return {
        storeId,
        storeType,
        organizationId: orgId,
        companyId: companyIdForApi,
      }
    }
    if (!orgId || companyIdForApi == null) return null
    return {
      storeId,
      storeType,
      organizationId: orgId,
      companyId: companyIdForApi,
    }
  }, [storeId, storeType, orgId, companyIdForApi])

  const {
    data: posClassifications = { categories: [], totalProducts: 0 },
    isLoading: isLoadingPOS,
  } = usePOSClassifications(posClassificationsPayload)
  const fetchedPOSCategories = posClassifications.categories
  const posTotalProducts = posClassifications.totalProducts

  const { mutateAsync: createCategoryMutation, isPending: isCreatingCategory } =
    useCreateCategory(storeId)
  const { mutateAsync: updateCategoryMutation, isPending: isUpdatingCategory } =
    useUpdateCategory(storeId)
  const { mutateAsync: deleteCategoryMutation, isPending: isDeletingCategory } =
    useDeleteCategory(storeId)
  const {
    mutateAsync: saveCategoryMappingMutation,
    isPending: isSavingMappings,
  } = useSaveCategoryMapping(storeId)
  const {
    mutateAsync: bulkUpdateVisibilityMutation,
    isPending: isBulkUpdatingVisibility,
  } = useBulkUpdateVisibility(storeId)
  const {
    mutateAsync: bulkDeleteCategoriesMutation,
    isPending: isBulkDeleting,
  } = useBulkDeleteCategories(storeId)
  const { data: categoryMappingDict = {} } = useCategoryMapping(storeId)

  const [webCategories, setWebCategories] = useState<WebCategory[]>([])

  useEffect(() => {
    if (fetchedWebCategories.length > 0) {
      setWebCategories(fetchedWebCategories)
    }
  }, [fetchedWebCategories])

  // Source of truth for the Mapping tab — derives `linkedPosCategories` by
  // joining the unified many-to-many dict from
  // GET /pantry/stores/:storeId/category-mapping against the in-memory
  // WebCategory tree for human-readable labels and category/subcategory kind.
  // Replaced the pre-2026-06-01 derivation that walked the embedded
  // cova_category / greenline_category / pos_mapping arrays — those are no
  // longer the source of truth for the Mapping tab. (The Web tab still reads
  // them for badge counts via useWebCategoriesWithPOS.)
  const linkedPosCategories = useMemo<LinkedPosCategoriesMap>(() => {
    // Index webCategories tree once (all depths): webId → ref with the full
    // `Root › Parent › Child` label + kind.
    const webRefById = new Map<string, LinkedWebCategoryRef>()
    const indexNodes = (nodes: WebCategory[], trail: string[]) => {
      nodes.forEach((wc) => {
        const id = String(wc.id)
        const path = [...trail, wc.displayName]
        webRefById.set(id, {
          id,
          label: path.join(' › '),
          kind: wc.depth === 0 ? 'category' : 'subcategory',
          parentId: wc.parentId != null ? String(wc.parentId) : undefined,
          depth: wc.depth,
        })
        indexNodes(wc.subcategories, path)
      })
    }
    indexNodes(webCategories, [])

    const result: LinkedPosCategoriesMap = {}
    Object.entries(categoryMappingDict).forEach(([posIdRaw, webIds]) => {
      const posId = String(posIdRaw)
      const refs: LinkedWebCategoryRef[] = []
      const seen = new Set<string>()
      webIds.forEach((webIdRaw) => {
        const webId = String(webIdRaw)
        const ref = webRefById.get(webId)
        // Skip unknown webIds — they'll resolve once useWebCategoriesWithPOS
        // hydrates, and the useMemo will recompute.
        if (!ref) return
        const dedupeKey = `${ref.kind}:${ref.id}`
        if (seen.has(dedupeKey)) return
        seen.add(dedupeKey)
        refs.push(ref)
      })
      if (refs.length === 0) return
      result[posId] = refs.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      )
    })
    return result
  }, [categoryMappingDict, webCategories])

  // Source of truth for the Web tab's "POS Mapping" column. Inverse
  // projection of the same unified dict: webId → POS refs linked to it.
  // Joins `categoryMappingDict` (posId → webIds[]) against the in-memory
  // POS-tree (`fetchedPOSCategories`) so each badge can render human-
  // readable POS category names. Replaces the previous Web-tab derivation
  // that read embedded cova_category / greenline_category / pos_mapping
  // arrays — those are no longer populated for stores on the unified
  // mapping flow, which caused the column to always show "Not mapped".
  const posMappingsByWebId = useMemo<PosMappingsByWebId>(() => {
    // Index POS-tree once: posId → ref with display label.
    const posRefById = new Map<string, LinkedPosCategoryRef>()
    fetchedPOSCategories.forEach((pc) => {
      const parentKey = String(pc.id)
      posRefById.set(parentKey, { id: parentKey, label: pc.name })
      pc.subcategories.forEach((sub) => {
        const subKey = String(sub.id)
        posRefById.set(subKey, {
          id: subKey,
          label: `${pc.name} › ${sub.name}`,
        })
      })
    })

    const result: PosMappingsByWebId = {}
    Object.entries(categoryMappingDict).forEach(([posIdRaw, webIds]) => {
      const posId = String(posIdRaw)
      const ref = posRefById.get(posId)
      // Skip unknown posIds — POS-tree may still be hydrating; the useMemo
      // will recompute once `fetchedPOSCategories` lands.
      if (!ref) return
      webIds.forEach((webIdRaw) => {
        const webId = String(webIdRaw)
        const bucket = result[webId] ?? []
        if (bucket.some((r) => r.id === ref.id)) return
        bucket.push(ref)
        result[webId] = bucket
      })
    })

    Object.keys(result).forEach((webId) => {
      result[webId] = result[webId].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      )
    })
    return result
  }, [categoryMappingDict, fetchedPOSCategories])

  // Adapter: the form's `PantryCategoryFormValues` carries `displayName`,
  // `headline`, `description`, `seoTitle`, `seoDescription`, `image`,
  // `bannerImage`. The gateway's `CreateCategoryInput` expects `name`, plus
  // the rest of those fields. The image-normalisation that legacy
  // `toItemData` performed (`_id` → `id`, `title` → `name`,
  // `alternative_text` → `alt`) is preserved here before the gateway's own
  // `toCategoryImage` runs.
  const normalizeImage = (value: unknown): unknown => {
    if (typeof value === 'string') return value
    if (!value || typeof value !== 'object') return undefined

    const image = value as {
      _id?: unknown
      id?: unknown
      src?: unknown
      url?: unknown
      name?: unknown
      title?: unknown
      alt?: unknown
      alternative_text?: unknown
    }

    const normalized: Record<string, unknown> = {
      name:
        typeof image.name === 'string'
          ? image.name
          : typeof image.title === 'string'
            ? image.title
            : '',
      src:
        typeof image.src === 'string'
          ? image.src
          : typeof image.url === 'string'
            ? image.url
            : '',
      alt:
        typeof image.alt === 'string'
          ? image.alt
          : typeof image.alternative_text === 'string'
            ? image.alternative_text
            : '',
    }
    if (image._id) normalized.id = image._id
    else if (image.id) normalized.id = image.id
    return normalized
  }

  const toCreateInput = (input: PantryCategoryFormValues) => ({
    name: input.displayName.trim(),
    slug: input.slug,
    // Lock state is surfaced through the gateway as `slug_locked` on
    // `item_data`. Persisting it lets the drawer re-open with the same lock
    // affordance after a save / reload — see categoryMappers.ts where it's
    // read back off `raw.slug_locked` into `WebCategory.slugLocked`.
    slugLocked: input.slugLocked,
    headline: input.headline,
    description: input.description,
    visible: input.visible,
    image: normalizeImage(input.image) as
      | { id?: string | number; name?: string; src?: string; alt?: string }
      | string
      | null
      | undefined,
    bannerImage: normalizeImage(input.bannerImage) as
      | { id?: string | number; name?: string; src?: string; alt?: string }
      | string
      | null
      | undefined,
    seoTitle: input.seoTitle,
    seoDescription: input.seoDescription,
    parentId: input.parentId,
  })

  const createCategory = async (input: PantryCategoryFormValues) =>
    createCategoryMutation(toCreateInput(input))

  const updateCategory = async (
    categoryId: string,
    input: PantryCategoryFormValues,
  ) => updateCategoryMutation({ categoryId, input: toCreateInput(input) })

  const deleteCategory = async (categoryId: string) =>
    deleteCategoryMutation(categoryId)

  const bulkUpdateVisibility = async (ids: string[], visible: boolean) =>
    bulkUpdateVisibilityMutation({ ids, visible })

  const bulkDeleteCategories = async (ids: string[]) =>
    bulkDeleteCategoriesMutation(ids)

  // Save path against the unified PUT /pantry/stores/:storeId/category-mapping
  // endpoint. Collapses the UI's flat Mapping[] into the many-to-many
  // posId → webIds[] dict the backend expects. No longer depends on
  // companyIdForApi (that field is still required for the POS classifications
  // read endpoint, but the save path is now POS-agnostic).
  const saveCategoryMappings = async (mappings: Mapping[]) => {
    if (!storeId) return Promise.resolve()

    const dict: CategoryMappingDict = {}
    mappings.forEach(({ posId, webId }) => {
      const key = String(posId)
      const value = String(webId)
      const bucket = dict[key] ?? []
      if (!bucket.includes(value)) {
        bucket.push(value)
        dict[key] = bucket
      }
    })

    return saveCategoryMappingMutation(dict)
  }

  const storeDomain = useMemo(() => storeDomainFromUrl(storeUrl), [storeUrl])

  const value = useMemo<CategoryContextValue>(
    () => ({
      webCategories,
      posCategories: fetchedPOSCategories,
      posTotalProducts,
      linkedPosCategories,
      posMappingsByWebId,
      isLoadingWeb,
      isLoadingPOS,
      isCreatingCategory,
      isUpdatingCategory,
      isDeletingCategory,
      isSavingMappings,
      isBulkUpdatingVisibility,
      isBulkDeleting,
      isMutatingCategory:
        isCreatingCategory ||
        isUpdatingCategory ||
        isDeletingCategory ||
        isBulkUpdatingVisibility ||
        isBulkDeleting,
      setWebCategories,
      createCategory,
      updateCategory,
      deleteCategory,
      bulkUpdateVisibility,
      bulkDeleteCategories,
      saveCategoryMappings,
      storeDomain,
      storeType,
    }),
    [
      webCategories,
      fetchedPOSCategories,
      posTotalProducts,
      linkedPosCategories,
      posMappingsByWebId,
      isLoadingWeb,
      isLoadingPOS,
      isCreatingCategory,
      isUpdatingCategory,
      isDeletingCategory,
      isSavingMappings,
      isBulkUpdatingVisibility,
      isBulkDeleting,
      storeDomain,
      storeType,
    ],
  )

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  )
}
